import React, { useEffect, useMemo, useRef, useState } from 'react';
import styles from './SearchableSelect.module.css';

// Plain custom combobox: a form-control-styled box that opens a searchable
// dropdown of {value, label} options on click. No external UI library.
const SearchableSelect = ({
  value,
  onChange,
  options = [],
  placeholder = 'Select',
  label,
  disabled = false,
  notFoundText = 'No results found',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef(null);
  const searchInputRef = useRef(null);
  const controlRef = useRef(null);

  const selectedOption = options.find(opt => opt.value === value);

  const filteredOptions = useMemo(() => {
    if (!search) return options;
    const lower = search.toLowerCase();
    return options.filter(opt => opt.label.toLowerCase().includes(lower));
  }, [options, search]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => searchInputRef.current?.focus(), 0);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [isOpen]);

  const handleToggle = () => {
    if (disabled) return;
    setIsOpen(prev => !prev);
    setSearch('');
  };

  const handleClose = () => {
    setIsOpen(false);
    setSearch('');
    controlRef.current?.focus();
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      handleClose();
    }
  };

  const handleSelect = (option) => {
    onChange(option.value);
    setIsOpen(false);
    setSearch('');
  };

  return (
    <div className={styles.container} ref={containerRef}>
      <div
        ref={controlRef}
        className={`${styles.control} ${disabled ? styles.disabled : ''} ${isOpen ? styles.focused : ''}`}
        onClick={handleToggle}
        tabIndex={disabled ? -1 : 0}
      >
        <span className={styles.textGroup}>
          {selectedOption && label && (
            <span className={styles.floatingLabel}>{label}</span>
          )}
          <span className={styles.valueRow}>
            <span className={`${styles.value} ${!selectedOption ? styles.placeholder : ''}`}>
              {selectedOption ? selectedOption.label : placeholder}
            </span>
            {selectedOption?.badge && (
              <span className={styles.badge}>{selectedOption.badge}</span>
            )}
          </span>
        </span>
        <span className={`${styles.arrow} ${isOpen ? styles.arrowOpen : ''}`}>&#9662;</span>
      </div>

      {isOpen && (
        <div className={styles.dropdown}>
          <div className={styles.searchWrapper}>
            <span className={styles.searchIcon}>&#128269;</span>
            <input
              ref={searchInputRef}
              type="text"
              className={styles.searchInput}
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={handleSearchKeyDown}
            />
          </div>
          <div className={styles.optionsList}>
            {filteredOptions.length === 0 ? (
              <div className={styles.noResults}>{notFoundText}</div>
            ) : (
              filteredOptions.map(option => (
                <div
                  key={option.value}
                  className={`${styles.option} ${option.value === value ? styles.selectedOption : ''}`}
                  onClick={() => handleSelect(option)}
                >
                  <span className={styles.optionLabel}>{option.label}</span>
                  {option.badge && (
                    <span className={`${styles.badge} ${option.value === value ? styles.badgeOnSelected : ''}`}>
                      {option.badge}
                    </span>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;
